import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import useSeo from "../lib/useSeo";
import api, { formatApiErrorDetail } from "../lib/api";
import { CONTACT } from "../lib/content";
import ObfuscatedEmail from "../components/ObfuscatedEmail";

// International email standard + South African mobile number validation.
const saPhone = /^(?:\+?27|0)\d{9}$/;

const schema = z.object({
  first_name: z.string().trim().min(1, "First name is required").max(80),
  last_name: z.string().trim().min(1, "Last name is required").max(80),
  email: z.string().trim().min(1, "Email is required").email("Please enter a valid email address"),
  phone: z
    .string()
    .trim()
    .min(1, "Cellphone number is required")
    .transform((v) => v.replace(/[\s\-()]/g, ""))
    .refine((v) => saPhone.test(v), "Enter a valid South African mobile number (e.g. 079 485 2928)"),
  message: z.string().trim().min(10, "Please provide at least 10 characters").max(5000),
  company_website: z.string().max(0).optional().or(z.literal("")), // honeypot
});

const fields = [
  { name: "first_name", label: "First Name", type: "text", placeholder: "Thabo", half: true },
  { name: "last_name", label: "Last Name", type: "text", placeholder: "Nkosi", half: true },
  { name: "email", label: "Email Address", type: "email", placeholder: "you@example.com", half: true },
  { name: "phone", label: "Cellphone Number", type: "tel", placeholder: "079 485 2928", half: true },
];

export default function Contact() {
  useSeo({
    title: "Contact Us",
    description:
      "Contact IntoLaw for affordable legal consulting. Secure enquiry form available to visitors within South Africa. General enquiries: intolaw@praxio.co.za.",
    path: "/contact",
  });

  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { company_website: "" } });

  const GENERIC_ERROR =
    "Something went wrong while sending your message. Please try again or reach out to us directly at intolaw@praxio.co.za.";

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post("/contact", values);
      // Backend returns 200 with success=false when the email could not be
      // delivered — treat this as a graceful failure, never a success.
      if (data && data.success === false) {
        toast.error(data.message || GENERIC_ERROR);
        return;
      }
      toast.success(data.message || "Thank you. Your enquiry has been received.");
      setSubmitted(true);
      reset();
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        // Country restriction — show the server's specific message.
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || GENERIC_ERROR);
      } else if (status === 429) {
        toast.error("You've sent a few messages already. Please try again a little later.");
      } else {
        // Any other error (5xx, network, Cloudflare, timeout) — never surface
        // raw errors to the visitor; show the friendly custom message.
        toast.error(GENERIC_ERROR);
      }
    }
  };

  return (
    <section className="px-6 pt-40 pb-24 lg:px-10 lg:pt-48">
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12 lg:gap-20">
        {/* Left: info */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-gold">Contact</p>
            <h1 className="font-display text-5xl font-semibold leading-[0.98] tracking-tight text-navy sm:text-6xl">
              Let&apos;s talk about your matter.
            </h1>
            <p className="mt-6 max-w-md leading-relaxed text-slate-600">
              Reach out for affordable, practical legal consulting. Complete the form and our team will be in touch shortly.
            </p>
          </Reveal>

          <div className="mt-12 space-y-8">
            <Reveal delay={0.05}>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bone text-gold"><Mail size={20} /></div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">General Enquiries</p>
                  <ObfuscatedEmail user="intolaw" domain="praxio.co.za" className="text-lg font-medium text-navy hover:text-gold transition-colors" data-testid="contact-email-display" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bone text-gold"><MessageCircle size={20} /></div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">WhatsApp</p>
                  <a href={`https://wa.me/${CONTACT.whatsappNumber}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="text-lg font-medium text-navy hover:text-gold transition-colors">
                    {CONTACT.whatsappDisplay}
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bone text-gold"><MapPin size={20} /></div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Address</p>
                  <p className="text-navy">31 5th Street, Commercia, Midrand, 1685, South Africa</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] lg:p-10">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  data-testid="contact-success"
                >
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <Mail size={28} />
                  </div>
                  <h2 className="font-display text-3xl font-medium text-navy">Enquiry received</h2>
                  <p className="mt-3 max-w-sm text-slate-600">
                    Thank you for reaching out to IntoLaw. We will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-new-enquiry"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-medium text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
                  >
                    Submit another enquiry
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="contact-form">
                  {/* Honeypot — visually hidden, off-screen */}
                  <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
                    <label>
                      Company Website
                      <input type="text" tabIndex={-1} autoComplete="off" {...register("company_website")} />
                    </label>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    {fields.map((f) => (
                      <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                        <label htmlFor={f.name} className="mb-2 block text-sm font-medium text-navy">
                          {f.label} <span className="text-gold">*</span>
                        </label>
                        <input
                          id={f.name}
                          type={f.type}
                          placeholder={f.placeholder}
                          data-testid={`contact-${f.name.replace("_", "-")}`}
                          aria-invalid={!!errors[f.name]}
                          {...register(f.name)}
                          className="w-full rounded-xl border border-black/10 bg-bone px-4 py-3 text-navy outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/40"
                        />
                        {errors[f.name] && (
                          <p className="mt-1.5 text-sm text-red-600" data-testid={`error-${f.name.replace("_", "-")}`}>
                            {errors[f.name].message}
                          </p>
                        )}
                      </div>
                    ))}

                    <div className="sm:col-span-2">
                      <label htmlFor="message" className="mb-2 block text-sm font-medium text-navy">
                        How can we help you? <span className="text-gold">*</span>
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        placeholder="Tell us a little about your matter…"
                        data-testid="contact-message"
                        aria-invalid={!!errors.message}
                        {...register("message")}
                        className="w-full resize-none rounded-xl border border-black/10 bg-bone px-4 py-3 text-navy outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/40"
                      />
                      {errors.message && (
                        <p className="mt-1.5 text-sm text-red-600" data-testid="error-message">{errors.message.message}</p>
                      )}
                    </div>
                  </div>

                  <p className="mt-5 text-xs text-slate-400">
                    Protected against spam and abuse. Your details are handled in line with POPIA.
                    Our online enquiry service is available to visitors within South Africa.
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-medium text-white transition-colors duration-300 hover:bg-royal disabled:opacity-60 sm:w-auto"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={18} className="animate-spin" /> Sending…</>
                    ) : (
                      <>Send Enquiry <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
